/**
 * Skill lifecycle: invoke → (optional clarifying/confirm turns) → done → auto-revoke → normal chat.
 *
 * Multi-step skills (SIM/PSS) often ask "Are you ready?" first — those must NOT revoke.
 * Partial / truncated deliverables must NOT revoke.
 */

/** Safety: never keep a skill sticky forever in one chat. */
export const MAX_SKILL_ASSISTANT_TURNS = 10

/** Confirm / clarify patterns — keep skill even if the reply is long. */
const KEEP_SKILL_PATTERNS = [
  /are you ready/i,
  /ready for (detailed )?(sim|pss|customer profile)/i,
  /nature of (your |my )?business/i,
  /which role/i,
  /what role/i,
  /which (customer )?segment/i,
  /confirm(ation)?/i,
  /before i (build|create|start|proceed)/i,
  /shall i (proceed|continue|start)/i,
  /do you want me to/i,
  /missing (detail|info|information)/i,
  /tell me (more|your|the)/i,
  /\?\s*$/m,
]

/** Strong evidence the framework deliverable was actually produced. */
const COMPLETE_PATTERNS: Record<string, RegExp[]> = {
  role_clarity: [/role title/i, /\bkra/i, /measurement metric/i],
  elevator_pitch: [/left\s*brain/i, /right\s*brain/i],
  elevator_script: [/left\s*brain/i, /right\s*brain/i],
  customer_profile: [/demographic/i, /psychographic/i],
  product_funnel: [/easy to buy/i, /high value/i],
  customer_ndp: [/\bneeds\b/i, /\bdesires\b/i, /\bproblems\b/i],
  // SIM: require end-of-map signals — partial Awareness alone must not revoke
  sim: [/\bawareness\b/i, /\bconsideration\b/i, /\bdecision\b/i, /\bretention\b/i, /step\s*8\b/i],
  pss: [/process structure/i, /\bfunction\b/i],
}

/** Minimum length for a "done" deliverable (varies by skill weight). */
const DONE_MIN_CHARS: Record<string, number> = {
  role_clarity: 700,
  elevator_pitch: 400,
  elevator_script: 400,
  customer_profile: 700,
  product_funnel: 600,
  customer_ndp: 900,
  sim: 2500,
  pss: 2000,
  _default: 800,
}

/** How many COMPLETE_PATTERNS must hit before we consider the skill done. */
const NEED_HITS: Record<string, number> = {
  role_clarity: 2,
  elevator_pitch: 2,
  elevator_script: 2,
  customer_profile: 2,
  product_funnel: 2,
  customer_ndp: 3,
  sim: 5, // all 4 stages + Step 8
  pss: 2,
  _default: 1,
}

export const isLikelySkillClarifyingReply = (
  assistantText: string,
  skillId?: string
): boolean => {
  const t = assistantText.trim()
  if (!t) return true
  if (KEEP_SKILL_PATTERNS.some((re) => re.test(t))) return true

  // SIM/PSS: short/medium replies with a question are still gathering context
  if ((skillId === 'sim' || skillId === 'pss') && t.length < (DONE_MIN_CHARS[skillId] ?? 2000)) {
    if ((t.match(/\?/g) || []).length >= 1) return true
  }

  const questionMarks = (t.match(/\?/g) || []).length
  return questionMarks >= 1 && t.length < 500
}

const markerHits = (text: string, skillId: string): number => {
  const patterns = COMPLETE_PATTERNS[skillId] || []
  return patterns.filter((re) => re.test(text)).length
}

export const isLikelySkillComplete = (
  assistantText: string,
  skillId?: string,
  opts?: { truncated?: boolean }
): boolean => {
  const t = assistantText.trim()
  if (!t) return false
  // Truncated stream = deliverable not finished — keep skill sticky
  if (opts?.truncated) return false
  if (isLikelySkillClarifyingReply(t, skillId)) return false

  const id = skillId || '_default'
  const minChars = DONE_MIN_CHARS[id] ?? DONE_MIN_CHARS._default
  const hits = skillId ? markerHits(t, skillId) : 0
  const needHits = NEED_HITS[id] ?? NEED_HITS._default

  if (t.length >= minChars && hits >= needHits) return true
  if (!skillId && t.length >= DONE_MIN_CHARS._default && !t.includes('?')) return true
  return false
}

/**
 * After an assistant reply under an active skill, should we revoke?
 * Keep skill only while Athena is still clarifying / confirming.
 * Once a real answer (or deliverable) is sent, clear skill_id.
 */
export const shouldAutoRevokeSkill = (
  assistantText: string,
  skillAssistantTurnsIncludingThis: number,
  skillId?: string,
  opts?: { truncated?: boolean }
): boolean => {
  if (opts?.truncated) return false
  if (skillAssistantTurnsIncludingThis >= MAX_SKILL_ASSISTANT_TURNS) return true
  // Still gathering context — keep skill sticky for the next turn
  if (isLikelySkillClarifyingReply(assistantText, skillId)) return false
  // Answer / deliverable delivered — drop skill mode
  return true
}
